import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { NavParams, ModalController, Platform } from '@ionic/angular';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LanguageService } from 'src/app/services/language.service';
import { DataService } from 'src/app/services/data.service';

@Component({
    selector: 'app-exercisemodal',
    templateUrl: './exercisemodal.component.html',
    styleUrls: ['./exercisemodal.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExercisemodalComponent implements OnInit {
  // private readonly testFullscreenVideoUrl = 'https://player.vimeo.com/video/76979871?dnt=1&playsinline=1&fullscreen=1';
  private readonly defaultVimeoVideoPath = '/407508746';

  platform = inject(Platform);
  data = inject(DataService);
  modalController = inject(ModalController);
  sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private languageService = inject(LanguageService);

  video: any;
  exercise: any;
  reseter: any;

  public response:any;
  public basepath:any;
  public redirecturl:any;
  public vimeourl?: SafeResourceUrl | null;
  public levels:any =[];

  /** Transition state */
  isLoading = false;
  /** True while a level-change transition is in flight — disables nav buttons */
  isBusy = false;
  private _iframeLoaded = false;
  private _iframeLoadResolve: (() => void) | null = null;

  ngOnInit() {
    this.vimeourl = this.vimeolink() || null;
  }

  onIframeLoaded(): void {
    this._iframeLoaded = true;
    this._iframeLoadResolve?.();
    this._iframeLoadResolve = null;
  }

  /** Returns a promise that resolves when iframe loads or after maxWait ms */
  private waitForIframeLoad(maxWait = 3000): Promise<void> {
    if (this._iframeLoaded) return Promise.resolve();
    return new Promise(resolve => {
      this._iframeLoadResolve = resolve;
      setTimeout(resolve, maxWait);
    });
  }

  async tooeasy(){
    if (this.exercise.istop || this.isBusy) return;
    await this.changeLevel('+');
  }

  async toohard(){
    if (this.exercise.istail || this.isBusy) return;
    await this.changeLevel('-');
  }

  private async changeLevel(direction: string) {
    this.isBusy = true;
    this.isLoading = true;
    this.cdr.markForCheck();

    const response: any = await this.data.exerciselevel(this.exercise, direction);
    if (!response) {
      this.isLoading = false;
      this.isBusy = false;
      this.cdr.markForCheck();
      return;
    }

    this._iframeLoaded = false;
    this.exercise = response.exercise;
    this.video = this.exercise['video' + this.exercise.getCurrentLevel];
    this.vimeourl = this.vimeolink();

    this.data.setEventsAnalytics('Exercise Video Change', 'event', {
      exercise_id: this.exercise.id,
      video_id: this.video ? this.video.id : '',
      level: this.exercise.getCurrentLevel,
      direction: direction === '+' ? 'next' : 'previous'
    });

    this.data.exerciseLevel.emit("changed", response.exercise);
    this.reseter(this.exercise);
    this.cdr.detectChanges();

    this.waitForIframeLoad(5000).then(() => {
      setTimeout(() => {
        this.isLoading = false;
        this.isBusy = false;
        this.cdr.markForCheck();
      }, 100);
    });
  }

  get step(){
    // return typeof this.exercise.map;
    let current = 1;
    for(let i = 0; i < this.exercise.map.length; i++){
      if(this.exercise.getCurrentLevel == this.exercise.map[i]){
        current = i+1;
      }
    }

    return current + "/" + this.exercise.map.length;
    return "1/12";
  }

  private get videoRatio(): number {
    if (this.video?.width && this.video?.height && this.video.width > 0 && this.video.height > 0) {
      return this.video.width / this.video.height;
    }
    return 16 / 9; // default: landscape 16:9
  }

  platformWidth(): number {
    const ratio = this.videoRatio;
    if (ratio >= 1) {
      // Landscape: cap at 550px, shrink on narrow screens (32px total margin)
      return Math.min(window.innerWidth - 32, 550);
    } else {
      // Portrait: derive from height
      return Math.round(this.platformHeight() * ratio);
    }
  }

  platformHeight(): number {
    const ratio = this.videoRatio;
    if (ratio >= 1) {
      // Landscape: height derived from width
      return Math.round(this.platformWidth() / ratio);
    } else {
      // Portrait: cap at available height
      return Math.min(window.innerHeight - 100, 480);
    }
  }

  vimeowidth(): number {
    return this.platformWidth();
  }

  vimeoheight(): number {
    return this.platformHeight();
  }

  get isNativeIosApp(): boolean {
    return this.platform.is('ios') && this.platform.is('hybrid');
  }

  private buildVimeoPlayerUrl(): string {
    const rawVideoPath = this.video?.vimeovideo;
    const normalizedVideoPath = rawVideoPath
      ? (rawVideoPath.startsWith('/') ? rawVideoPath : `/${rawVideoPath}`)
      : this.defaultVimeoVideoPath;
    const url = new URL(`https://player.vimeo.com/video${normalizedVideoPath}`);
    const extraParams = this.video?.extra_params;

    if (extraParams) {
      const normalizedExtraParams = extraParams.startsWith('?') || extraParams.startsWith('&')
        ? extraParams.slice(1)
        : extraParams;

      new URLSearchParams(normalizedExtraParams).forEach((value, key) => {
        url.searchParams.set(key, value);
      });
    }

    url.searchParams.set('dnt', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('fullscreen', '1');
    if (this.languageService.currentLang === 'cy') {
      url.searchParams.set('audiotrack', 'cy');
    } else {
      url.searchParams.delete('audiotrack');
    }

    return url.toString();
  }

  vimeolink(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.buildVimeoPlayerUrl());
  }

  cancel(){
    this.modalController.dismiss();
    this.data.exerciseLevel.emit("changed");
  }
}
